import { useContext, useEffect, useRef, useState } from 'react';
import { AlertColor, Card, Grid, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import { RecordAccessLogsType } from '../../utils/type';
import { Formik, FormikProps, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';
import {
  createAndUpdateRecordAccessLogs,
  deleteRecordAccessLogs,
  downloadRecordAccessLog,
  getAllRecordAccessLogs,
} from '../../services/configApi/recordAccessLog';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import DatePickerComponent from '../../components/formlib/DatePickerComponent';
import Textinput from '../../components/formlib/Textinput';
import dayjs, { Dayjs } from 'dayjs';
import CommentTextbox from '../../components/formlib/CommentTextbox';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import MediumTypography from '../../components/formlib/MediumTypography';
import { ReactComponent as Delete } from '../../assets/images/deleteAlertIcon.svg';
import { ReactComponent as DownloadIcon } from '../../assets/images/downloadIconGreen.svg';
import {
  EmployeeListType,
  getAllEmployeeMinimalList,
} from '../../services/ifspClient/IfspApi';
import { useIntl } from 'react-intl';
import EmptyScreen from '../../components/shared/EmptyScreen';
import ModalPopup from '../../components/formlib/ModalPopup';
import { UnSavedChangesContext } from '../../context/UnSavedChangesProvider';
import { useMultiplePermissions } from '../../hooks/usePermissions';
import { REFERRAL_STATUS_KEY } from '../../services/Constant';
import { ApiError, isCustomError } from '../../services/ApiResponseHandler';
import { getClientDocumentFileName } from '../../utils/fileUtils';

const validationSchema = Yup.array().of(
  Yup.object().shape({
    dateOfAccess: Yup.mixed().required('Date of Access is required').nullable(),

    requestedBy: Yup.string()
      .trim()
      .required('Requested By is required')
      .max(100, 'Maximum 100 characters allowed'),

    purpose: Yup.string()
      .trim()
      .required('Purpose is required')
      .max(1000, 'Maximum 1000 characters allowed'),
  }),
);

const RecordAccessLog = () => {
  const [recordAccessLogs, setRecordAccessLogs] = useState<
    RecordAccessLogsType[]
  >([]);
  const [initialFormValues, setInitialFormValues] = useState<
    RecordAccessLogsType[]
  >([]);
  const [employeeList, setEmployeeList] = useState<EmployeeListType[]>([]);
  const [open, setOpen] = useState(false);
  const [recordAccessLogDeleteModal, setRecordAccessLogDeleteModal] =
    useState<boolean>(false);
  const [deleteRecordAccessLogId, setDeleteRecordAccessLogId] =
    useState<string>('');
  const [activeCancelButton, setActiveCancelButton] = useState<string | null>(
    null,
  );

  const [showUnsavedAlertDialog, setShowUnsavedAlertDialog] =
    useState<boolean>(false);
  const [showEmptyScreen, setShowEmptyScreen] = useState<boolean>(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');

  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const { handleUpdateUnsavedChanges } = useContext(UnSavedChangesContext);
  const formRef = useRef<FormikProps<RecordAccessLogsType[]>>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const intl = useIntl();

  useEffect(() => {
    fetchRecordAccessLogs();
  }, []);

  const permissions = useMultiplePermissions([
    'backend.clients',
    'backend.in_active_referrals',
  ]);
  const referralStatus = localStorage.getItem(REFERRAL_STATUS_KEY) ?? 'None';
  const hasEditPermission =
    referralStatus === 'Inactive'
      ? permissions['backend.clients'].canEdit &&
        permissions['backend.in_active_referrals'].canEdit
      : permissions['backend.clients'].canEdit ||
        permissions['backend.in_active_referrals'].canEdit;

  useEffect(() => {
    if (!hasEditPermission) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          e.preventDefault();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [hasEditPermission]);

  const FormChangeWatcher = () => {
    const { values } = useFormikContext<RecordAccessLogsType[]>();

    useEffect(() => {
      const hasChanges = values.some((row) => {
        const initialRow = initialFormValues.find((r) => r.uiId === row.uiId);

        return !isRowUnchanged(row, initialRow);
      });

      handleUpdateUnsavedChanges(hasChanges);
    }, [values, initialFormValues]);

    return null;
  };

  const fetchRecordAccessLogs = async () => {
    try {
      toggleLoader(true);

      const response = await getAllRecordAccessLogs();

      const updatedData = response.map((item) => ({
        ...item,
        uiId: uuidv4(),
      }));

      // Update local state
      setRecordAccessLogs(updatedData);
      setInitialFormValues(updatedData);

      setShowEmptyScreen(response.length === 0);

      // IMPORTANT: Sync Formik
      setTimeout(() => {
        formRef.current?.resetForm({
          values: updatedData,
        });
      }, 0);

      // Your existing logic
      const ids = [
        ...new Set(response.map((item) => item.createdBy)),
        ...new Set(response.map((item) => item.modifiedBy)),
      ];

      getAllEmployees(ids as string[]);
    } catch {
      setOpen(true);
      setToastrVariable('error');
      setToastrId('RecordAccessLog.FetchRecordAccessLogsError');
      setToastrDefaultMessage('Failed to fetch record access logs');
    } finally {
      toggleLoader(false);
    }
  };

  const getAllEmployees = async (ids: string[]) => {
    try {
      toggleLoader(true);
      const response = await getAllEmployeeMinimalList(ids);
      setEmployeeList(response);
    } catch {
      setOpen(true);
      setToastrVariable('error');
      setToastrId('RecordAccessLog.FetchEmployeeError');
      setToastrDefaultMessage('Failed to fetch employee list');
    } finally {
      toggleLoader(false);
    }
  };

  const handleAddAndUpdate = (
    data: RecordAccessLogsType,
    activatedSaveButton: string,
  ) => {
    toggleLoader(true);

    createAndUpdateRecordAccessLogs(data)
      .then((res: RecordAccessLogsType) => {
        setOpen(true);
        setToastrVariable('success');
        setToastrId(
          data.id
            ? 'RecordAccessLog.UpdateRecordAccessLogsSuccess'
            : 'RecordAccessLog.CreateRecordAccessLogsSuccess',
        );
        setToastrDefaultMessage(
          data.id
            ? 'Record access logs updated successfully'
            : 'Record access logs created successfully',
        );

        const formValues = formRef.current?.values;

        if (formValues) {
          const index = formValues.findIndex(
            (row) => row.uiId === activatedSaveButton,
          );

          if (index === -1) return;

          const existingRow = formValues[index];

          const updatedValues = [...formValues];

          updatedValues[index] = {
            ...res,
            uiId: existingRow.uiId,
          };

          formRef.current.setValues(updatedValues);

          setInitialFormValues((prev) => {
            const exists = prev.some((row) => row.uiId === activatedSaveButton);

            const updatedRow = updatedValues.find(
              (r) => r.uiId === activatedSaveButton,
            );

            if (!updatedRow) return prev;

            // If exists → update
            if (exists) {
              return prev.map((row) =>
                row.uiId === activatedSaveButton ? updatedRow : row,
              );
            }

            // If new row → ADD it
            return [...prev, updatedRow];
          });
        }
      })
      .catch(() => {
        setOpen(true);
        setToastrVariable('error');
        setToastrId(
          data.id
            ? 'RecordAccessLog.UpdateRecordAccessLogsError'
            : 'RecordAccessLog.CreateRecordAccessLogsError',
        );
        setToastrDefaultMessage(
          data.id
            ? 'Failed to update record access logs'
            : 'Failed to create record access logs',
        );
      })
      .finally(() => {
        toggleLoader(false);
      });
  };

  const handleDeleteRecordAccessLog = () => {
    toggleLoader(true);
    deleteRecordAccessLogs(deleteRecordAccessLogId)
      .then(() => {
        setOpen(true);
        setToastrVariable('success');
        setToastrId('RecordAccessLog.DeleteRecordAccessLogsSuccess');
        setToastrDefaultMessage('Record access logs deleted successfully');
        setRecordAccessLogDeleteModal(false);
        fetchRecordAccessLogs();
      })
      .catch(() => {
        toggleLoader(false);
        setOpen(true);
        setToastrVariable('error');
        setToastrId('RecordAccessLog.DeleteRecordAccessLogsError');
        setToastrDefaultMessage('Failed to delete record access logs');
      })
      .finally(() => {
        setRecordAccessLogDeleteModal(false);
      });
  };

  const handleRowSubmit = async (index: number) => {
    const fields = [
      `[${index}].dateOfAccess`,
      `[${index}].requestedBy`,
      `[${index}].purpose`,
    ];

    for (const field of fields) {
      formRef.current?.setFieldTouched(field, true); // IMPORTANT
      await formRef.current?.validateField(field);
    }

    const errors = formRef.current?.errors;

    if (errors?.[index]) {
      return;
    }

    const rowData = formRef.current?.values[index];
    if (!rowData) return;
    handleAddAndUpdate(rowData, rowData.uiId);
  };

  const getEmployeeName = (id?: string) => {
    const employee = employeeList.find((emp) => emp.id === id);
    if (!employee) return '-';

    return `${employee.lastName}, ${employee.firstName}`;
  };

  const handleDownloadRecordAccessLogs = () => {
    toggleLoader(true);
    downloadRecordAccessLog()
      .then((response) => {
        const blob = new Blob([response], { type: 'application/pdf' });

        // Create a Blob URL
        const blobUrl = URL.createObjectURL(blob);

        // Open the Blob URL in a new tab
        const anchor = document.createElement('a');
        anchor.href = blobUrl;
        anchor.target = '_blank';
        anchor.download = getClientDocumentFileName('Record Access Logs.pdf'); // Set desired file name here

        // Append anchor to document body
        document.body.appendChild(anchor);

        // Trigger anchor click
        anchor.click();

        window.open(blobUrl, '_blank');

        // Clean up after use
        URL.revokeObjectURL(blobUrl);
        document.body.removeChild(anchor);

        toggleLoader(false);
      })
      .catch((error) => {
        toggleLoader(false);
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('ConsentForms.downloadErrorMsg');
          setToastrDefaultMessage('Failed to download');
        }
      });
  };

  const handleClose = (reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const getInitialRowByUiId = (
    uiId: string,
    initialValues: RecordAccessLogsType[],
  ) => {
    return initialValues.find((item) => item.uiId === uiId);
  };

  const isRowUnchanged = (
    current: RecordAccessLogsType,
    initial?: RecordAccessLogsType,
  ) => {
    // NEW ROW (no initial)
    if (!initial) {
      return !current.dateOfAccess && !current.requestedBy && !current.purpose;
    }

    // EXISTING ROW
    return (
      current.dateOfAccess === initial.dateOfAccess &&
      current.requestedBy === initial.requestedBy &&
      current.purpose === initial.purpose
    );
  };

  return (
    <Formik
      initialValues={recordAccessLogs}
      innerRef={formRef}
      validationSchema={validationSchema}
      onSubmit={() => {}}
    >
      {({ values, setFieldValue, errors, touched }) => (
        <Box
          ref={parentRef}
          style={{
            pointerEvents: hasEditPermission ? 'auto' : 'none',
            opacity: hasEditPermission ? 'unset' : '0.8',
          }}
        >
          <FormChangeWatcher />
          {showEmptyScreen && (
            <EmptyScreen
              titleLabelId="RecordAccessLog.emptyScreenTitle"
              defaultTitleText="No Logs added yet."
              buttonLabelId="RecordAccessLog.emptyScreenButton"
              defaultButtonTitleText="Add New Log"
              showButton={hasEditPermission}
              onButtonClick={() => {
                setToastrId('');
                const uiId = uuidv4();
                setRecordAccessLogs([
                  {
                    dateOfAccess: null,
                    requestedBy: '',
                    purpose: '',
                    createdBy: '',
                    modifiedBy: null,
                    createdOn: '',
                    modifiedOn: '',
                    uiId: uiId,
                    id: '',
                  },
                ]);
                formRef.current?.setValues([
                  {
                    dateOfAccess: null,
                    requestedBy: '',
                    purpose: '',
                    createdBy: '',
                    modifiedBy: null,
                    createdOn: '',
                    modifiedOn: '',
                    uiId: uiId,
                    id: '',
                  },
                ]);
                setShowEmptyScreen(false);
              }}
            />
          )}
          {!showEmptyScreen && (
            <Card className="formCardview">
              {values.some((item) => item?.id) && (
                <Box className="flex__ mb-sm mr-xs justifyContent-FlexEnd alignItemCenter">
                  <Box
                    className="flex__ alignItemCenter cursorPointer"
                    onClick={handleDownloadRecordAccessLogs}
                  >
                    <DownloadIcon />
                    <MediumTypography
                      fontSize="16px"
                      className="ml-xs"
                      textColor={'#008C82'}
                      labelid="RecordAccessLog.DownloadRecordAccessLog"
                      defaultlabel="Download all logs"
                    />
                  </Box>
                </Box>
              )}
              <ModalPopup
                open={showUnsavedAlertDialog}
                onCancel={() => {
                  setShowUnsavedAlertDialog(false);
                }}
                description="formUnsavedChangesMessage"
                onOk={() => {
                  const formValues = formRef.current?.values;

                  if (formValues && activeCancelButton) {
                    const updatedValues = formValues
                      .filter((row) => {
                        if (row.uiId === activeCancelButton) {
                          const initialRow = getInitialRowByUiId(
                            activeCancelButton,
                            initialFormValues,
                          );

                          if (!initialRow) {
                            return false;
                          }
                        }
                        return true;
                      })
                      .map((row) => {
                        if (row.uiId === activeCancelButton) {
                          return (
                            getInitialRowByUiId(
                              activeCancelButton,
                              initialFormValues,
                            ) || row
                          );
                        }
                        return row;
                      });

                    formRef.current?.setValues(updatedValues);
                    setInitialFormValues((prev) => prev);

                    setShowEmptyScreen(updatedValues.length === 0);
                  }

                  handleUpdateUnsavedChanges(false);
                  setShowUnsavedAlertDialog(false);
                }}
                labelId1="Clientpage.cancelbtn"
                negativeActionLabel="cancelText"
                labelId2="Clientpage.Okbtn"
                positiveActionLabel="Ok"
              />
              <ModalPopup
                open={recordAccessLogDeleteModal}
                onClose={() => setRecordAccessLogDeleteModal(false)}
                description={'Are you sure you want to delete this log?'}
                labelId1="Clientpage.cancelbtn"
                defaultDescription="Are you sure you want to delete this log?"
                negativeActionLabel="cancelText"
                labelId2="Clientpage.Okbtn"
                positiveActionLabel="deleteText"
                onOk={() => {
                  handleDeleteRecordAccessLog();
                }}
                onCancel={() => {
                  setRecordAccessLogDeleteModal(false);
                }}
              />
              <SnackBarComponent
                open={open && !!toastrId}
                handleClose={handleClose}
                successOrError={toastrVariable}
                labelId={toastrId}
                defaultMessageId={toastrDefaultMessage}
              />
              {values?.map((item, sectionIndex) => (
                <Box
                  key={item.id}
                  className={'borderRadius4 p-lg mb-md'}
                  sx={{
                    backgroundColor: '#ECF9F8',
                    border: '1px solid #00C6B8',
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={5}>
                      <DatePickerComponent
                        labelid="RecordAccessLog.DateOfAccess"
                        defaultlabelid="Date of Access"
                        className="bg__white"
                        value={
                          item?.dateOfAccess ? dayjs(item.dateOfAccess) : null
                        }
                        handlechange={(value: Dayjs | null) => {
                          const formatedDate = value
                            ? value.format('MM/DD/YYYY')
                            : '';
                          if (formatedDate) {
                            setFieldValue(
                              `[${sectionIndex}].dateOfAccess`,
                              formatedDate,
                            );
                          } else {
                            setFieldValue(`[${sectionIndex}].dateOfAccess`, '');
                          }
                        }}
                        required
                      />
                      {touched?.[sectionIndex]?.dateOfAccess &&
                        typeof errors?.[sectionIndex]?.dateOfAccess ===
                          'string' && (
                          <MediumTypography
                            labelid={errors?.[sectionIndex]?.dateOfAccess}
                            defaultlabel={'Date of Access is required'}
                            className="errorText-md"
                          />
                        )}
                    </Grid>
                    <Grid item xs={5}>
                      <Textinput
                        labelid="RecordAccessLog.RequestedBy"
                        defaultlabelid="Requested By"
                        className="bg__white"
                        Value={item?.requestedBy ? item.requestedBy : ''}
                        inputProps={{
                          maxLength: 100,
                        }}
                        handlechange={(value) => {
                          if (value) {
                            setFieldValue(
                              `[${sectionIndex}].requestedBy`,
                              value,
                            );
                          } else {
                            setFieldValue(`[${sectionIndex}].requestedBy`, '');
                          }
                        }}
                        Required
                      />
                      {touched?.[sectionIndex]?.requestedBy &&
                        typeof errors?.[sectionIndex]?.requestedBy ===
                          'string' && (
                          <MediumTypography
                            labelid={errors?.[sectionIndex]?.requestedBy}
                            defaultlabel={'Requested By is required'}
                            className="errorText-md"
                          />
                        )}
                    </Grid>
                    {item?.id && (
                      <Grid
                        item
                        xs={2}
                        className="mt-xs flex__ justifyContent-FlexEnd"
                      >
                        <Delete
                          className="cursorPointer"
                          onClick={() => {
                            setDeleteRecordAccessLogId(item?.id);
                            setRecordAccessLogDeleteModal(true);
                          }}
                        />
                      </Grid>
                    )}
                    <Grid item xs={10}>
                      <CommentTextbox
                        name="Purpose"
                        placeholder={intl.formatMessage({
                          id: 'RecordAccessLog.Purpose',
                          defaultMessage: 'Purpose',
                        })}
                        maxLength={500}
                        Value={item?.purpose ? item.purpose : ''}
                        handlechange={(value) => {
                          if (value) {
                            setFieldValue(`[${sectionIndex}].purpose`, value);
                          } else {
                            setFieldValue(`[${sectionIndex}].purpose`, '');
                          }
                        }}
                        Required
                      />
                      {touched?.[sectionIndex]?.purpose &&
                        typeof errors?.[sectionIndex]?.purpose === 'string' && (
                          <MediumTypography
                            labelid={errors?.[sectionIndex]?.purpose}
                            defaultlabel={'Purpose is required'}
                            className="errorText-md"
                          />
                        )}
                    </Grid>
                  </Grid>
                  <Box className="flex__ justifyContentSpaceBtw alignItemCenter mt-md">
                    <Box>
                      {item?.id && (
                        <Box className="flex__ alignItemCenter">
                          <MediumTypography
                            labelid="RecordAccessLog.CreatedByLabel"
                            defaultlabel="Created By:"
                            sxProps={{ marginRight: '6px' }}
                          />

                          <Box className="flex__ alignItemCenter">
                            <Tooltip
                              title={getEmployeeName(item?.createdBy || '')}
                              arrow
                              followCursor
                              className="minWidthContent"
                            >
                              <Box>
                                <MediumTypography
                                  label={getEmployeeName(item?.createdBy || '')}
                                  sxProps={{
                                    marginRight: '4px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '200px',
                                  }}
                                />
                              </Box>
                            </Tooltip>

                            <MediumTypography
                              label={`on ${item?.createdOn || '-'}`}
                            />
                          </Box>
                        </Box>
                      )}
                      {item?.modifiedBy && item?.modifiedOn && (
                        <Box className="flex__ alignItemCenter">
                          <MediumTypography
                            labelid="RecordAccessLog.LastUpdatedByLabel"
                            defaultlabel="Last Updated By:"
                            sxProps={{ marginRight: '6px' }}
                          />

                          <Box className="flex__ alignItemCenter">
                            <Tooltip
                              title={getEmployeeName(item?.modifiedBy)}
                              arrow
                              followCursor
                              className="minWidthContent"
                            >
                              <Box>
                                <MediumTypography
                                  label={getEmployeeName(item?.modifiedBy)}
                                  sxProps={{
                                    marginRight: '4px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '200px',
                                  }}
                                />
                              </Box>
                            </Tooltip>
                            <MediumTypography
                              label={`on ${item?.modifiedOn}`}
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                    <Box>
                      <ButtonComponent
                        className="btn-primary btn-cancel mr-sm"
                        variantType="contained"
                        defaultLabelId="Cancel"
                        labelId="Waivers.cancel"
                        onClick={() => {
                          const initialRow = getInitialRowByUiId(
                            item.uiId,
                            initialFormValues,
                          );

                          const hasChanges = !isRowUnchanged(item, initialRow);

                          if (hasChanges) {
                            setActiveCancelButton(item.uiId); // store uiId instead of index
                            setShowUnsavedAlertDialog(true);
                          } else {
                            setShowUnsavedAlertDialog(false);
                          }
                        }}
                      />
                      <ButtonComponent
                        className="btn-primary btn-submit"
                        variantType="contained"
                        type="submit"
                        labelId="Waivers.submit"
                        defaultLabelId="Submit"
                        onClick={() => handleRowSubmit(sectionIndex)}
                        disabled={
                          !!item.id &&
                          isRowUnchanged(
                            item,
                            getInitialRowByUiId(item.uiId, initialFormValues),
                          )
                        }
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
              {values.length > 0 && (
                <MediumTypography
                  labelid="RecordAccessLog.AddAnotherLog"
                  defaultlabel="+ Add Another Access Log"
                  fontSize="16px"
                  fontweight={700}
                  textColor={hasEditPermission ? '#00C6B8' : '#97A6A5'}
                  sxProps={{
                    cursor: hasEditPermission ? 'pointer' : 'not-allowed',
                    visibility: hasEditPermission ? 'visible' : 'hidden',
                  }}
                  className="cursorPointer mt-md"
                  onClick={() => {
                    const uiId = uuidv4();
                    setRecordAccessLogs((prev) => [
                      ...prev,
                      {
                        dateOfAccess: null,
                        requestedBy: '',
                        purpose: '',
                        createdBy: '',
                        modifiedBy: null,
                        createdOn: '',
                        modifiedOn: '',
                        uiId,
                        id: '',
                      },
                    ]);
                    formRef.current?.setValues([
                      ...values,
                      {
                        dateOfAccess: null,
                        requestedBy: '',
                        purpose: '',
                        createdBy: '',
                        modifiedBy: null,
                        createdOn: '',
                        modifiedOn: '',
                        uiId,
                        id: '',
                      },
                    ]);
                  }}
                />
              )}
            </Card>
          )}
        </Box>
      )}
    </Formik>
  );
};

export default RecordAccessLog;
